import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Skeleton,
  Typography,
  Link,
  IconButton,
  Tooltip,
  Alert,
  AlertTitle,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  Snackbar,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from './Sidebar.jsx';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWorkflowRunHistoryThunk } from '../../state/slices/dashboardSlice';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import WorkflowConnectionsDialog from '../../components/WorkflowConnectionsDialog.jsx';
import PropTypes from 'prop-types';
import LogsViewer from '../../workflows/LogsViewer';

const pad = (n) => String(n).padStart(2, '0');
const formatLocalDate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const getWeeklyDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 7);
  return { from: formatLocalDate(from), to: formatLocalDate(to) };
};

const getDailyDateRange = (dateStr) => {
  const d = dateStr ? new Date(dateStr) : new Date();
  const date = formatLocalDate(d);
  return { from: date, to: date };
};

const statusColorMap = {
  SUCCESS: {
    bg: '#10B981',
    bgLight: '#D1FAE5',
    text: '#065F46',
    border: '#10B981',
  },
  FAILED: {
    bg: '#EF4444',
    bgLight: '#FEE2E2',
    text: '#991B1B',
    border: '#EF4444',
  },
  ACTIVE: {
    bg: '#3B82F6',
    bgLight: '#DBEAFE',
    text: '#1E40AF',
    border: '#3B82F6',
  },
  PARTIAL_SUCCESS: {
    bg: '#F59E0B',
    bgLight: '#FEF3C7',
    text: '#92400E',
    border: '#F59E0B',
  },
  CANCELLED: {
    bg: '#A3A3A3',
    bgLight: '#E5E7EB',
    text: '#1F2937',
    border: '#A3A3A3',
  },
};

// Helper function to calculate execution time in seconds
const calculateExecutionTime = (startTime, endTime) => {
  if (!startTime) return null;
  const start = new Date(startTime).getTime();
  const end = endTime ? new Date(endTime).getTime() : Date.now();
  if (isNaN(start) || isNaN(end)) return null;
  return Math.max(0, (end - start) / 1000); // Return in seconds
};

// Helper function to format execution time
const formatExecutionTime = (seconds) => {
  if (seconds === null || seconds === undefined) return 'Running';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  if (seconds < 3600)
    return `${Math.floor(seconds / 60)}m ${(seconds % 60).toFixed(0)}s`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

// Helper function to calculate throughput (records per second)
const calculateThroughput = (successCount, failureCount, executionTime) => {
  if (!executionTime || executionTime === 0) return 0;
  const totalRecords = (successCount || 0) + (failureCount || 0);
  return totalRecords / executionTime;
};

// Helper function to calculate health score (0-100)
const calculateHealthScore = (successCount, failureCount, recentTrend = 0) => {
  const total = (successCount || 0) + (failureCount || 0);
  if (total === 0) return 50; // Neutral score for no data
  const successRate = (successCount / total) * 100;
  const baseScore = successRate * 0.8; // 80% weight on success rate
  const trendBonus = Math.min(20, Math.max(-20, recentTrend * 2)); // 20% weight on trend, capped
  return Math.min(100, Math.max(0, baseScore + trendBonus));
};

// Helper function to get health score color
const getHealthScoreColor = (score) => {
  if (score >= 80) return '#10B981'; // Green
  if (score >= 50) return '#F59E0B'; // Yellow/Orange
  return '#EF4444'; // Red
};

// Helper function to calculate percentiles
const calculatePercentile = (sortedArray, percentile) => {
  if (!sortedArray || sortedArray.length === 0) return 0;
  const index = Math.ceil((percentile / 100) * sortedArray.length) - 1;
  return sortedArray[Math.max(0, Math.min(index, sortedArray.length - 1))];
};

// Helper function to detect anomalies using IQR method
const detectAnomalies = (values) => {
  if (!values || values.length < 4) return [];
  const sorted = [...values].sort((a, b) => a - b);
  const q1 = calculatePercentile(sorted, 25);
  const q3 = calculatePercentile(sorted, 75);
  const iqr = q3 - q1;
  const lowerBound = q1 - 1.5 * iqr;
  const upperBound = q3 + 1.5 * iqr;

  return values
    .map((value, index) => ({
      value,
      index,
      isAnomaly: value < lowerBound || value > upperBound,
    }))
    .filter((item) => item.isAnomaly)
    .map((item) => item.index);
};

// Helper function to get date range for quick filters
const getDateRangeForPreset = (preset) => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const from = new Date();

  switch (preset) {
    case 'today':
      from.setHours(0, 0, 0, 0);
      break;
    case 'last7days':
      from.setDate(today.getDate() - 7);
      from.setHours(0, 0, 0, 0);
      break;
    case 'last30days':
      from.setDate(today.getDate() - 30);
      from.setHours(0, 0, 0, 0);
      break;
    case 'thismonth':
      from.setDate(1);
      from.setHours(0, 0, 0, 0);
      break;
    case 'lastmonth': {
      from.setMonth(today.getMonth() - 1);
      from.setDate(1);
      from.setHours(0, 0, 0, 0);
      const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
      today.setMonth(today.getMonth() - 1);
      today.setDate(lastDay.getDate());
      today.setHours(23, 59, 59, 999);
      break;
    }
    default:
      return getWeeklyDateRange();
  }

  return { from: formatLocalDate(from), to: formatLocalDate(today) };
};

const Reports = () => {
  const dispatch = useDispatch();
  const { workflowRunHistory: storeRunHistory = [] } = useSelector(
    (state) => state.dashboard || {},
  );
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.4,
        type: 'spring',
        stiffness: 100,
      },
    },
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: index * 0.05,
        duration: 0.3,
        ease: 'easeOut',
      },
    }),
    hover: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.2,
        ease: 'easeInOut',
      },
    },
  };

  const { from: initialFrom, to: initialTo } = getWeeklyDateRange();
  const [filters, setFilters] = useState({
    range: 'weekly',
    from_date: initialFrom,
    to_date: initialTo,
    limit: 100,
    runMode: 'ALL',
    recordType: 'ALL',
  });
  const [search, setSearch] = useState('');
  const [limitError, setLimitError] = useState('');
  const [connOpen, setConnOpen] = useState(false);
  const [connWorkflowName, setConnWorkflowName] = useState('');
  const [connLoading, setConnLoading] = useState(false);
  const [connError, setConnError] = useState('');
  const [connData, setConnData] = useState(null);

  // Logs viewer state
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedWorkflowRunId, setSelectedWorkflowRunId] = useState(null);

  // Data state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [workflowConnections, setWorkflowConnections] = useState({});

  // Sorting state for table
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: 'none',
  });

  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 seconds default
  const [lastUpdated, setLastUpdated] = useState(null);
  const refreshIntervalRef = useRef(null);

  // Previous period data for comparison
  const [previousPeriodData, setPreviousPeriodData] = useState([]);

  // Track if component has mounted to skip initial auto-apply
  const isMountedRef = useRef(false);
  const debounceTimerRef = useRef(null);

  // Toast state for workflow selection
  const [showWorkflowToast, setShowWorkflowToast] = useState(false);
  const [selectedWorkflowName, setSelectedWorkflowName] = useState('');

  const loadData = async (filtersToUse = null) => {
    // Use provided filters or current filters state
    const activeFilters = filtersToUse || filters;
    setLoading(true);
    setError('');
    try {
      const params = {
        range: activeFilters.range,
        limit: activeFilters.limit,
        from_date: activeFilters.from_date || undefined,
        to_date: activeFilters.to_date || undefined,
      };
      // Always fetch fresh run history to ensure latest workflows are visible
      const data = await dispatch(
        fetchWorkflowRunHistoryThunk(params),
      ).unwrap();
      setRows(Array.isArray(data) ? data : []);
      setLastUpdated(new Date());
      // Build connections map from run-history metadata (no separate API calls)
      if (Array.isArray(data) && data.length > 0) {
        const connectionsMap = {};
        for (const run of data) {
          const wfId = run?.workflow_id;
          const meta = run?.metadata || {};
          const sources = Array.isArray(meta.sources) ? meta.sources : [];
          const destinations = Array.isArray(meta.destinations)
            ? meta.destinations
            : [];
          if (wfId) {
            connectionsMap[wfId] = {
              connections: { sources, destinations },
            };
          }
        }
        setWorkflowConnections(connectionsMap);
      }

      // Fetch previous period data for comparison if dates are set
      if (activeFilters.from_date && activeFilters.to_date) {
        loadPreviousPeriodData(activeFilters.from_date, activeFilters.to_date);
      }
    } catch (e) {
      setError(e?.message || 'Failed to fetch run history');
    } finally {
      setLoading(false);
    }
  };

  const loadPreviousPeriodData = async (fromDate, toDate) => {
    try {
      const currentFrom = new Date(fromDate);
      const currentTo = new Date(toDate);
      const daysDiff = Math.ceil(
        (currentTo - currentFrom) / (1000 * 60 * 60 * 24),
      );

      const previousFrom = new Date(currentFrom);
      previousFrom.setDate(previousFrom.getDate() - daysDiff - 1);
      const previousTo = new Date(currentFrom);
      previousTo.setDate(previousTo.getDate() - 1);

      // Format dates for API
      const formatDateForAPI = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const prevParams = {
        limit: filters.limit || 100, // Use same limit or default to 100
        from_date: formatDateForAPI(previousFrom),
        to_date: formatDateForAPI(previousTo),
      };

      const prevData = await dispatch(
        fetchWorkflowRunHistoryThunk(prevParams),
      ).unwrap();

      setPreviousPeriodData(Array.isArray(prevData) ? prevData : []);
    } catch (e) {
      console.warn('Failed to fetch previous period data:', e);
      setPreviousPeriodData([]); // Set empty array on error
    }
  };

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(() => {
        loadData();
      }, refreshInterval);
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    } else {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    }
  }, [autoRefresh, refreshInterval]);

  useEffect(() => {
    loadData();
    // Mark as mounted after initial load
    setTimeout(() => {
      isMountedRef.current = true;
    }, 100);
  }, []);

  // Auto-apply filter changes with debounce (only for API-related filters)
  useEffect(() => {
    // Skip on initial mount
    if (!isMountedRef.current) {
      return;
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the loadData call by 500ms to avoid too many API calls
    debounceTimerRef.current = setTimeout(() => {
      loadData();
    }, 500);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filters.range, filters.from_date, filters.to_date, filters.limit]);

  const handleChange = (key) => (e) => {
    const value = e.target.value;
    if (key === 'limit') {
      const numValue = Number(value);
      if (value === '' || isNaN(numValue)) {
        setLimitError('');
        setFilters((prev) => ({ ...prev, [key]: value }));
      } else if (numValue > 100) {
        setLimitError('Maximum limit is 100');
        setFilters((prev) => ({ ...prev, [key]: 100 }));
      } else if (numValue < 1) {
        setLimitError('');
        setFilters((prev) => ({ ...prev, [key]: 1 }));
      } else {
        setLimitError('');
        setFilters((prev) => ({ ...prev, [key]: numValue }));
      }
    } else {
      setFilters((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleRangeChange = (e) => {
    const value = e.target.value;
    if (value === 'weekly') {
      const { from, to } = getWeeklyDateRange();
      setFilters((prev) => ({
        ...prev,
        range: value,
        from_date: from,
        to_date: to,
        limit: 20,
      }));
    } else if (value === 'daily') {
      const { from, to } = getDailyDateRange();
      setFilters((prev) => ({
        ...prev,
        range: value,
        from_date: from,
        to_date: to,
        limit: 20,
      }));
    } else {
      setFilters((prev) => ({ ...prev, range: value }));
    }
  };

  const handleFromDateChange = (e) => {
    const val = e.target.value;
    setFilters((prev) => {
      // If range is daily, change it to weekly when date is changed
      const newRange = prev.range === 'daily' ? 'weekly' : prev.range;
      return {
        ...prev,
        range: newRange,
        from_date: val,
      };
    });
  };

  const handleToDateChange = (e) => {
    const val = e.target.value;
    setFilters((prev) => {
      // If range is daily, change it to weekly when date is changed
      const newRange = prev.range === 'daily' ? 'weekly' : prev.range;
      return {
        ...prev,
        range: newRange,
        to_date: val,
      };
    });
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows;
    if (q) {
      list = list.filter((r) =>
        (r?.workflow_name || '').toLowerCase().includes(q),
      );
    }

    if (filters.runMode === 'SCHEDULED') {
      list = list.filter((r) => !!r?.is_scheduled);
    } else if (filters.runMode === 'ON DEMAND') {
      list = list.filter((r) => !r?.is_scheduled);
    }

    if (filters.recordType === 'SUCCESS') {
      list = list.filter((r) => Number(r?.success_record_count || 0) > 0);
    } else if (filters.recordType === 'FAILED') {
      list = list.filter((r) => Number(r?.failure_record_count || 0) > 0);
    }

    return list;
  }, [rows, search, filters.runMode, filters.recordType]);

  // Helper function to get connection names
  const getConnectionNames = (workflowId) => {
    const connData = workflowConnections[workflowId];
    if (!connData || !connData.connections) {
      return { source: [], destination: [] };
    }

    // Extract source connection names
    const sources = connData.connections.sources || [];
    const sourceNames = sources.map((s) => s?.connection_name).filter(Boolean);

    // Extract destination connection names
    const destinations = connData.connections.destinations || [];
    const destNames = destinations
      .map((d) => d?.connection_name)
      .filter(Boolean);

    return {
      source: sourceNames.length > 0 ? sourceNames : ['-'],
      destination: destNames.length > 0 ? destNames : ['-'],
    };
  };

  // Apply sorting to filteredRows
  const sortedRows = useMemo(() => {
    if (sortConfig.direction === 'none' || !sortConfig.column) {
      return filteredRows;
    }

    return [...filteredRows].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.column) {
        case 'workflow_name':
          aValue = (a.workflow_name || '').toLowerCase();
          bValue = (b.workflow_name || '').toLowerCase();
          break;
        case 'status':
          aValue = (a.status || a.run_status || '').toLowerCase();
          bValue = (b.status || b.run_status || '').toLowerCase();
          break;
        case 'run_mode':
          aValue = a.is_scheduled ? 'scheduled' : 'on demand';
          bValue = b.is_scheduled ? 'scheduled' : 'on demand';
          break;
        case 'date':
          aValue = a.start_time ? new Date(a.start_time).getTime() : 0;
          bValue = b.start_time ? new Date(b.start_time).getTime() : 0;
          break;
        case 'source':
          {
            const aSource = getConnectionNames(a.workflow_id).source;
            const bSource = getConnectionNames(b.workflow_id).source;
            aValue = (aSource[0] === '-' ? '' : aSource[0] || '').toLowerCase();
            bValue = (bSource[0] === '-' ? '' : bSource[0] || '').toLowerCase();
          }
          break;
        case 'destination':
          {
            const aDest = getConnectionNames(a.workflow_id).destination;
            const bDest = getConnectionNames(b.workflow_id).destination;
            aValue = (aDest[0] === '-' ? '' : aDest[0] || '').toLowerCase();
            bValue = (bDest[0] === '-' ? '' : bDest[0] || '').toLowerCase();
          }
          break;
        case 'success':
          aValue = Number(a.success_record_count || 0);
          bValue = Number(b.success_record_count || 0);
          break;
        case 'failure':
          aValue = Number(a.failure_record_count || 0);
          bValue = Number(b.failure_record_count || 0);
          break;
        case 'execution_time':
          {
            const aTime = calculateExecutionTime(
              a.start_time,
              a.end_time || a.metadata?.end_time,
            );
            const bTime = calculateExecutionTime(
              b.start_time,
              b.end_time || b.metadata?.end_time,
            );
            aValue = aTime || 0;
            bValue = bTime || 0;
          }
          break;
        case 'throughput':
          {
            const aTime = calculateExecutionTime(
              a.start_time,
              a.end_time || a.metadata?.end_time,
            );
            const bTime = calculateExecutionTime(
              b.start_time,
              b.end_time || b.metadata?.end_time,
            );
            const aThroughput = calculateThroughput(
              a.success_record_count || 0,
              a.failure_record_count || 0,
              aTime,
            );
            const bThroughput = calculateThroughput(
              b.success_record_count || 0,
              b.failure_record_count || 0,
              bTime,
            );
            aValue = aThroughput;
            bValue = bThroughput;
          }
          break;
        case 'health_score':
          {
            const aScore = calculateHealthScore(
              a.success_record_count || 0,
              a.failure_record_count || 0,
            );
            const bScore = calculateHealthScore(
              b.success_record_count || 0,
              b.failure_record_count || 0,
            );
            aValue = aScore;
            bValue = bScore;
          }
          break;
        default:
          return 0;
      }

      if (
        sortConfig.column === 'date' ||
        sortConfig.column === 'success' ||
        sortConfig.column === 'failure' ||
        sortConfig.column === 'execution_time' ||
        sortConfig.column === 'throughput' ||
        sortConfig.column === 'health_score'
      ) {
        // For dates and numbers
        return sortConfig.direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      } else {
        // For strings
        if (sortConfig.direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
    });
  }, [filteredRows, sortConfig, workflowConnections]);

  const groupedWorkflows = useMemo(() => {
    const groups = new Map();

    sortedRows.forEach((row) => {
      const key = `${row.workflow_name}_${row.workflow_id}`;
      if (!groups.has(key)) {
        groups.set(key, {
          workflow_name: row.workflow_name,
          workflow_id: row.workflow_id,
          runs: [],
          runCount: 0,
        });
      }
      const group = groups.get(key);
      group.runs.push(row);
      group.runCount = group.runs.length;
    });

    return Array.from(groups.values());
  }, [sortedRows]);

  const toggleGroup = (workflowName, workflowId) => {
    const key = `${workflowName}_${workflowId}`;
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Sorting handler
  const handleSort = (column) => {
    let direction = 'asc';

    if (sortConfig.column === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (
      sortConfig.column === column &&
      sortConfig.direction === 'desc'
    ) {
      direction = 'none';
      column = null;
    }

    setSortConfig({ column, direction });
  };

  const workflowAgg = useMemo(() => {
    const map = new Map();
    sortedRows.forEach((r) => {
      const name = r?.workflow_name || 'Unknown';
      const s = Number(r?.success_record_count || 0);
      const f = Number(r?.failure_record_count || 0);
      const id = r?.workflow_id;
      const cur = map.get(name) || { success: 0, failure: 0, workflow_id: id };
      cur.success += s;
      cur.failure += f;
      if (!cur.workflow_id && id) cur.workflow_id = id;
      map.set(name, cur);
    });
    const rows = Array.from(map.entries()).map(([name, v]) => ({
      name,
      success: v.success,
      failure: v.failure,
      total: v.success + v.failure,
      workflow_id: v.workflow_id,
    }));
    rows.sort((a, b) => b.total - a.total);
    const maxTotal = rows.reduce((m, a) => Math.max(m, a.total), 0);
    return { rows, maxTotal };
  }, [sortedRows]);

  // KPI Calculations - Record-based and Run-based metrics
  const kpiData = useMemo(() => {
    // Record-based metrics
    const totalSuccessRecords = sortedRows.reduce(
      (sum, r) => sum + (Number(r?.success_record_count) || 0),
      0,
    );
    const totalFailureRecords = sortedRows.reduce(
      (sum, r) => sum + (Number(r?.failure_record_count) || 0),
      0,
    );
    const totalRecords = totalSuccessRecords + totalFailureRecords;
    const recordSuccessRate =
      totalRecords > 0 ? (totalSuccessRecords / totalRecords) * 100 : 0;
    const recordFailureRate =
      totalRecords > 0 ? (totalFailureRecords / totalRecords) * 100 : 0;

    // Run-based metrics
    const totalRuns = sortedRows.length;
    const successfulRuns = sortedRows.filter((r) => {
      const status = (r.status || r.run_status || '').toUpperCase();
      return (
        status === 'SUCCESS' && (Number(r?.failure_record_count) || 0) === 0
      );
    }).length;
    const failedRuns = sortedRows.filter((r) => {
      const status = (r.status || r.run_status || '').toUpperCase();
      return (
        status === 'FAILED' ||
        (status === 'SUCCESS' && (Number(r?.failure_record_count) || 0) > 0)
      );
    }).length;
    const runSuccessRate =
      totalRuns > 0 ? (successfulRuns / totalRuns) * 100 : 0;
    const runFailureRate = totalRuns > 0 ? (failedRuns / totalRuns) * 100 : 0;

    // Execution breakdown
    const scheduledRuns = sortedRows.filter(
      (r) => r?.is_scheduled === true,
    ).length;
    const onDemandRuns = sortedRows.filter(
      (r) => r?.is_scheduled === false || !r?.is_scheduled,
    ).length;

    return {
      // Record metrics
      totalRecords,
      totalSuccessRecords,
      totalFailureRecords,
      recordSuccessRate: Number(recordSuccessRate.toFixed(1)),
      recordFailureRate: Number(recordFailureRate.toFixed(1)),
      // Run metrics
      totalRuns,
      successfulRuns,
      failedRuns,
      runSuccessRate: Number(runSuccessRate.toFixed(1)),
      runFailureRate: Number(runFailureRate.toFixed(1)),
      // Execution breakdown
      scheduledRuns,
      onDemandRuns,
    };
  }, [sortedRows]);

  // Time-series data for line chart
  const timeSeriesData = useMemo(() => {
    const dateMap = new Map();

    sortedRows.forEach((run) => {
      if (!run.start_time) return;
      const date = formatLocalDate(new Date(run.start_time));
      const existing = dateMap.get(date) || {
        date,
        success: 0,
        failure: 0,
        count: 0,
      };
      existing.success += Number(run.success_record_count || 0);
      existing.failure += Number(run.failure_record_count || 0);
      existing.count += 1;
      dateMap.set(date, existing);
    });

    return Array.from(dateMap.values())
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((item) => ({
        ...item,
        total: item.success + item.failure,
      }));
  }, [sortedRows]);

  // Status distribution data
  const statusDistribution = useMemo(() => {
    const statusCounts = {
      SUCCESS: 0,
      FAILED: 0,
      ACTIVE: 0,
      PARTIAL_SUCCESS: 0,
      CANCELLED: 0,
    };

    sortedRows.forEach((run) => {
      const status = (
        run.status ||
        run.run_status ||
        'CANCELLED'
      ).toUpperCase();
      if (Object.prototype.hasOwnProperty.call(statusCounts, status)) {
        statusCounts[status] += 1;
      }
    });

    const total = Object.values(statusCounts).reduce(
      (sum, count) => sum + count,
      0,
    );

    return Object.entries(statusCounts)
      .filter(([, count]) => count > 0)
      .map(([status, count]) => ({
        name: status.replace('_', ' '),
        value: count,
        percentage: total > 0 ? ((count / total) * 100).toFixed(1) : 0,
      }));
  }, [sortedRows]);

  // Period comparison data - Enhanced with both record and run metrics
  const periodComparison = useMemo(() => {
    if (!filters.from_date || !filters.to_date) return null;

    const currentFrom = new Date(filters.from_date);
    const currentTo = new Date(filters.to_date);
    const daysDiff = Math.ceil(
      (currentTo - currentFrom) / (1000 * 60 * 60 * 24),
    );

    const previousFrom = new Date(currentFrom);
    previousFrom.setDate(previousFrom.getDate() - daysDiff - 1);
    const previousTo = new Date(currentFrom);
    previousTo.setDate(previousTo.getDate() - 1);

    // Format date ranges for display
    const formatDateRange = (from, to) => {
      const fromStr = from.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      const toStr = to.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      return `${fromStr} - ${toStr}`;
    };

    // Filter data for current period
    const currentPeriodData = sortedRows.filter((run) => {
      if (!run.start_time) return false;
      const runDate = new Date(run.start_time);
      return runDate >= currentFrom && runDate <= currentTo;
    });

    // Use previousPeriodData state if available, otherwise filter from rows
    const prevData =
      previousPeriodData.length > 0
        ? previousPeriodData
        : rows.filter((run) => {
            if (!run.start_time) return false;
            const runDate = new Date(run.start_time);
            return runDate >= previousFrom && runDate < currentFrom;
          });

    const calculatePeriodMetrics = (data) => {
      // Record metrics
      const successRecords = data.reduce(
        (sum, r) => sum + (Number(r?.success_record_count) || 0),
        0,
      );
      const failureRecords = data.reduce(
        (sum, r) => sum + (Number(r?.failure_record_count) || 0),
        0,
      );
      const totalRecords = successRecords + failureRecords;
      const recordSuccessRate =
        totalRecords > 0 ? (successRecords / totalRecords) * 100 : 0;
      const recordFailureRate =
        totalRecords > 0 ? (failureRecords / totalRecords) * 100 : 0;

      // Run metrics
      const totalRuns = data.length;
      const successfulRuns = data.filter((r) => {
        const status = (r.status || r.run_status || '').toUpperCase();
        return (
          status === 'SUCCESS' && (Number(r?.failure_record_count) || 0) === 0
        );
      }).length;
      const failedRuns = data.filter((r) => {
        const status = (r.status || r.run_status || '').toUpperCase();
        return (
          status === 'FAILED' ||
          (status === 'SUCCESS' && (Number(r?.failure_record_count) || 0) > 0)
        );
      }).length;
      const runSuccessRate =
        totalRuns > 0 ? (successfulRuns / totalRuns) * 100 : 0;
      const runFailureRate = totalRuns > 0 ? (failedRuns / totalRuns) * 100 : 0;

      return {
        successRecords,
        failureRecords,
        totalRecords,
        recordSuccessRate: Number(recordSuccessRate.toFixed(1)),
        recordFailureRate: Number(recordFailureRate.toFixed(1)),
        totalRuns,
        successfulRuns,
        failedRuns,
        runSuccessRate: Number(runSuccessRate.toFixed(1)),
        runFailureRate: Number(runFailureRate.toFixed(1)),
      };
    };

    const current = calculatePeriodMetrics(currentPeriodData);
    const previous = calculatePeriodMetrics(prevData);

    const calculateChange = (current, previous) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return ((current - previous) / previous) * 100;
    };

    return {
      currentPeriod: formatDateRange(currentFrom, currentTo),
      previousPeriod: formatDateRange(previousFrom, previousTo),
      current: {
        ...current,
      },
      previous: {
        ...previous,
      },
      changes: {
        successRecords: calculateChange(
          current.successRecords,
          previous.successRecords,
        ),
        failureRecords: calculateChange(
          current.failureRecords,
          previous.failureRecords,
        ),
        totalRecords: calculateChange(
          current.totalRecords,
          previous.totalRecords,
        ),
        recordSuccessRate: calculateChange(
          current.recordSuccessRate,
          previous.recordSuccessRate,
        ),
        totalRuns: calculateChange(current.totalRuns, previous.totalRuns),
        successfulRuns: calculateChange(
          current.successfulRuns,
          previous.successfulRuns,
        ),
        failedRuns: calculateChange(current.failedRuns, previous.failedRuns),
        runSuccessRate: calculateChange(
          current.runSuccessRate,
          previous.runSuccessRate,
        ),
      },
    };
  }, [
    sortedRows,
    filters.from_date,
    filters.to_date,
    previousPeriodData,
    rows,
  ]);

  // Performance Percentiles (P50, P75, P95, P99)
  const performancePercentiles = useMemo(() => {
    const executionTimes = sortedRows
      .map((r) =>
        calculateExecutionTime(
          r.start_time,
          r.end_time || r.metadata?.end_time,
        ),
      )
      .filter((t) => t !== null && t > 0)
      .sort((a, b) => a - b);

    if (executionTimes.length === 0) {
      return { p50: 0, p75: 0, p95: 0, p99: 0 };
    }

    return {
      p50: calculatePercentile(executionTimes, 50),
      p75: calculatePercentile(executionTimes, 75),
      p95: calculatePercentile(executionTimes, 95),
      p99: calculatePercentile(executionTimes, 99),
    };
  }, [sortedRows]);

  // Hourly/Daily Performance Heatmap Data
  const performanceHeatmap = useMemo(() => {
    const heatmapData = [];
    const hourDayMap = new Map();

    sortedRows.forEach((run) => {
      if (!run.start_time) return;
      const date = new Date(run.start_time);
      const hour = date.getHours();
      const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayName = dayNames[dayOfWeek];
      const key = `${dayName}-${hour}`;

      if (!hourDayMap.has(key)) {
        hourDayMap.set(key, {
          day: dayName,
          hour,
          success: 0,
          failure: 0,
          total: 0,
          runs: 0,
        });
      }

      const entry = hourDayMap.get(key);
      entry.success += Number(run.success_record_count || 0);
      entry.failure += Number(run.failure_record_count || 0);
      entry.total +=
        Number(run.success_record_count || 0) +
        Number(run.failure_record_count || 0);
      entry.runs += 1;
    });

    // Calculate success rate for each hour-day combination
    hourDayMap.forEach((value) => {
      value.successRate =
        value.total > 0 ? (value.success / value.total) * 100 : 0;
      heatmapData.push(value);
    });

    return heatmapData.sort((a, b) => {
      const dayOrder = {
        Sun: 0,
        Mon: 1,
        Tue: 2,
        Wed: 3,
        Thu: 4,
        Fri: 5,
        Sat: 6,
      };
      if (dayOrder[a.day] !== dayOrder[b.day]) {
        return dayOrder[a.day] - dayOrder[b.day];
      }
      return a.hour - b.hour;
    });
  }, [sortedRows]);

  // Anomaly Detection
  const anomalies = useMemo(() => {
    const executionTimes = sortedRows
      .map((r) =>
        calculateExecutionTime(
          r.start_time,
          r.end_time || r.metadata?.end_time,
        ),
      )
      .filter((t) => t !== null && t > 0);

    const failureRates = sortedRows.map((r) => {
      const success = Number(r.success_record_count || 0);
      const failure = Number(r.failure_record_count || 0);
      const total = success + failure;
      return total > 0 ? (failure / total) * 100 : 0;
    });

    const anomalyIndices = detectAnomalies(executionTimes);
    const failureAnomalyIndices = detectAnomalies(failureRates);

    const detectedAnomalies = [];

    anomalyIndices.forEach((idx) => {
      const run = sortedRows[idx];
      if (run) {
        detectedAnomalies.push({
          type: 'execution_time',
          severity: 'high',
          workflowName: run.workflow_name || 'Unknown',
          workflowId: run.workflow_id,
          value: formatExecutionTime(executionTimes[idx]),
          message: `Unusually long execution time: ${formatExecutionTime(executionTimes[idx])}`,
        });
      }
    });

    failureAnomalyIndices.forEach((idx) => {
      const run = sortedRows[idx];
      if (run && failureRates[idx] > 50) {
        // Only flag if failure rate > 50%
        detectedAnomalies.push({
          type: 'failure_rate',
          severity: 'high',
          workflowName: run.workflow_name || 'Unknown',
          workflowId: run.workflow_id,
          value: `${failureRates[idx].toFixed(1)}%`,
          message: `Unusually high failure rate: ${failureRates[idx].toFixed(1)}%`,
        });
      }
    });

    return detectedAnomalies;
  }, [sortedRows]);

  // Top 5 Workflows by Total Records
  const topWorkflows = useMemo(() => {
    const workflowMap = new Map();

    sortedRows.forEach((run) => {
      const wfId = run.workflow_id;
      const wfName = run.workflow_name || 'Unknown';
      const key = `${wfName}_${wfId}`;

      if (!workflowMap.has(key)) {
        workflowMap.set(key, {
          workflowId: wfId,
          workflowName: wfName,
          totalRecords: 0,
          successRecords: 0,
          failureRecords: 0,
          totalRuns: 0,
          successfulRuns: 0,
          failedRuns: 0,
        });
      }

      const entry = workflowMap.get(key);
      entry.totalRecords +=
        Number(run.success_record_count || 0) +
        Number(run.failure_record_count || 0);
      entry.successRecords += Number(run.success_record_count || 0);
      entry.failureRecords += Number(run.failure_record_count || 0);
      entry.totalRuns += 1;

      const status = (run.status || run.run_status || '').toUpperCase();
      if (status === 'SUCCESS' && Number(run.failure_record_count || 0) === 0) {
        entry.successfulRuns += 1;
      } else {
        entry.failedRuns += 1;
      }
    });

    return Array.from(workflowMap.values())
      .sort((a, b) => b.totalRecords - a.totalRecords)
      .slice(0, 5)
      .map((wf) => ({
        ...wf,
        recordSuccessRate:
          wf.totalRecords > 0 ? (wf.successRecords / wf.totalRecords) * 100 : 0,
        runSuccessRate:
          wf.totalRuns > 0 ? (wf.successfulRuns / wf.totalRuns) * 100 : 0,
      }));
  }, [sortedRows]);

  // Top 5 Source Connections by Total Records
  const topSourceConnections = useMemo(() => {
    const connectionMap = new Map();

    sortedRows.forEach((run) => {
      const { source } = getConnectionNames(run.workflow_id);
      source.forEach((connName) => {
        if (connName === '-') return;

        if (!connectionMap.has(connName)) {
          connectionMap.set(connName, {
            connectionName: connName,
            totalRecords: 0,
            successRecords: 0,
            failureRecords: 0,
            totalRuns: 0,
            workflows: new Set(),
          });
        }

        const entry = connectionMap.get(connName);
        entry.totalRecords +=
          Number(run.success_record_count || 0) +
          Number(run.failure_record_count || 0);
        entry.successRecords += Number(run.success_record_count || 0);
        entry.failureRecords += Number(run.failure_record_count || 0);
        entry.totalRuns += 1;
        if (run.workflow_id) {
          entry.workflows.add(run.workflow_id);
        }
      });
    });

    return Array.from(connectionMap.values())
      .sort((a, b) => b.totalRecords - a.totalRecords)
      .slice(0, 5)
      .map((conn) => ({
        ...conn,
        workflowCount: conn.workflows.size,
        recordSuccessRate:
          conn.totalRecords > 0
            ? (conn.successRecords / conn.totalRecords) * 100
            : 0,
      }));
  }, [sortedRows, workflowConnections]);

  // Rolling Averages for Time-Series (7-day and 30-day) - Reserved for future use
  // const timeSeriesWithRollingAverages = useMemo(() => {
  //   if (timeSeriesData.length === 0) return [];
  //
  //   return timeSeriesData.map((item, index) => {
  //     // 7-day rolling average
  //     const start7Day = Math.max(0, index - 6);
  //     const last7Days = timeSeriesData.slice(start7Day, index + 1);
  //     const avg7Day = last7Days.length > 0
  //       ? last7Days.reduce((sum, d) => sum + d.success + d.failure, 0) / last7Days.length
  //       : 0;
  //
  //     // 30-day rolling average
  //     const start30Day = Math.max(0, index - 29);
  //     const last30Days = timeSeriesData.slice(start30Day, index + 1);
  //     const avg30Day = last30Days.length > 0
  //       ? last30Days.reduce((sum, d) => sum + d.success + d.failure, 0) / last30Days.length
  //       : 0;
  //
  //     return {
  //       ...item,
  //       rolling7Day: avg7Day,
  //       rolling30Day: avg30Day,
  //     };
  //   });
  // }, [timeSeriesData]);

  const WorkflowYAxisTick = ({ x, y, payload }) => (
    <text x={x} y={y} textAnchor="end" fill="#374151" style={{ fontSize: 12 }}>
      {payload?.value}
    </text>
  );

  WorkflowYAxisTick.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    payload: PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      index: PropTypes.number,
    }),
  };

  const openConnectionsById = async (id, name) => {
    setConnError('');
    setConnWorkflowName(name || '');
    setConnOpen(true);

    if (!id) {
      setConnError('Workflow ID not found.');
      setConnData(null);
      return;
    }

    // Check if we already have the data in workflowConnections state
    if (workflowConnections[id]) {
      setConnData(workflowConnections[id]);
      return;
    }
    // Derive from available run-history data; fetch via thunk only if missing
    try {
      setConnLoading(true);
      let derived = null;

      const sourceData = (
        Array.isArray(rows) && rows.length > 0 ? rows : storeRunHistory
      ).find((run) => run?.workflow_id === id)?.metadata;

      if (sourceData) {
        const sources = Array.isArray(sourceData.sources)
          ? sourceData.sources
          : [];
        const destinations = Array.isArray(sourceData.destinations)
          ? sourceData.destinations
          : [];
        derived = { connections: { sources, destinations } };
      }

      if (!derived && (!storeRunHistory || storeRunHistory.length === 0)) {
        const fetched = await dispatch(
          fetchWorkflowRunHistoryThunk({
            range: filters.range,
            limit: Math.max(filters.limit || 10, 100),
            from_date: filters.from_date || undefined,
            to_date: filters.to_date || undefined,
          }),
        ).unwrap();

        const meta = fetched?.find((run) => run?.workflow_id === id)?.metadata;
        if (meta) {
          const sources = Array.isArray(meta.sources) ? meta.sources : [];
          const destinations = Array.isArray(meta.destinations)
            ? meta.destinations
            : [];
          derived = { connections: { sources, destinations } };
        }
      }

      if (derived) {
        setConnData(derived);
        setWorkflowConnections((prev) => ({ ...prev, [id]: derived }));
      } else {
        setConnError('Connections not available in run history');
        setConnData(null);
      }
    } catch (e) {
      setConnError(e?.message || 'Failed to derive connections');
    } finally {
      setConnLoading(false);
    }
  };

  const handleViewLogs = (workflowRunId) => {
    setSelectedWorkflowRunId(workflowRunId);
    setLogsDialogOpen(true);
  };

  const handleDownloadCSV = () => {
    const headers = [
      'Sr. No',
      'Workflow Name',
      'Status',
      'Run Mode',
      'Date',
      'Source',
      'Destination',
      'Success Records',
      'Failure Records',
      'Execution Time',
      'Throughput (records/sec)',
      'Health Score',
      'Workflow Run ID',
    ];

    // Prepare CSV rows
    const csvRows = sortedRows.map((row, idx) => {
      const runMode = row?.is_scheduled ? 'SCHEDULED' : 'ON DEMAND';
      const startDate = row.start_time
        ? formatLocalDate(new Date(row.start_time))
        : '-';
      const { source, destination } = getConnectionNames(row.workflow_id);
      const execTime = calculateExecutionTime(
        row.start_time,
        row.end_time || row.metadata?.end_time,
      );
      const throughput = calculateThroughput(
        row.success_record_count || 0,
        row.failure_record_count || 0,
        execTime,
      );
      const healthScore = calculateHealthScore(
        row.success_record_count || 0,
        row.failure_record_count || 0,
      );

      return [
        idx + 1,
        `"${row.workflow_name || ''}"`,
        row.status || row.run_status || 'N/A',
        runMode,
        startDate,
        `"${source.join('; ')}"`,
        `"${destination.join('; ')}"`,
        row.success_record_count ?? 0,
        row.failure_record_count ?? 0,
        execTime ? formatExecutionTime(execTime) : 'Running',
        throughput > 0 ? throughput.toFixed(2) : '0',
        Math.round(healthScore),
        row.workflow_run_id || '',
      ].join(',');
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...csvRows].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `workflow_run_history_${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{ maxWidth: '100vw', overflowX: 'hidden' }}
    >
      <Box
        sx={{
          display: 'flex',
          maxWidth: '100vw',
          overflowX: 'hidden',
        }}
      >
        <Sidebar />
        <Box
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 3 },
            background: '#f8f9fa',
            minHeight: '100vh',
            overflowX: 'hidden',
            width: 0, // Prevents flex item from overflowing
          }}
        >
          {/* Header */}
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: '1.75rem',
                fontWeight: 700,
                color: '#111827',
              }}
            >
              Reports
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {lastUpdated && (
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    fontStyle: 'italic',
                  }}
                >
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </Typography>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#5965A6',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                        {
                          backgroundColor: '#5965A6',
                        },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {autoRefresh ? (
                      <PauseIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <PlayArrowIcon sx={{ fontSize: 16 }} />
                    )}
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      Auto-refresh
                    </Typography>
                  </Box>
                }
              />
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={refreshInterval}
                  onChange={(e) => setRefreshInterval(Number(e.target.value))}
                  disabled={!autoRefresh}
                  sx={{ fontSize: '0.75rem' }}
                >
                  <MenuItem value={30000}>30s</MenuItem>
                  <MenuItem value={60000}>1m</MenuItem>
                  <MenuItem value={300000}>5m</MenuItem>
                </Select>
              </FormControl>
              <Box
                sx={{
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              >
                <IconButton
                  size="small"
                  onClick={loadData}
                  disabled={loading}
                  sx={{
                    color: '#5965A6',
                    '&:hover': { backgroundColor: 'rgba(89, 101, 166, 0.08)' },
                  }}
                >
                  <RefreshIcon
                    sx={{
                      fontSize: 20,
                      animation: loading ? 'spin 1s linear infinite' : 'none',
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          </Box>

          {/* Filters */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid #E5E7EB',
              borderRadius: 2,
              mb: 2,
              background: 'linear-gradient(to bottom, #ffffff, #F9FAFB)',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.95rem',
                fontWeight: 600,
                color: '#374151',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 4,
                  height: 20,
                  backgroundColor: '#5965A6',
                  borderRadius: 1,
                  display: 'inline-block',
                }}
              />
              Filter Options
            </Typography>

            {/* Quick Filter Presets */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {[
                { label: 'Today', value: 'today' },
                { label: 'Last 7 Days', value: 'last7days' },
                { label: 'Last 30 Days', value: 'last30days' },
                { label: 'This Month', value: 'thismonth' },
                { label: 'Last Month', value: 'lastmonth' },
              ].map((preset) => {
                const range = getDateRangeForPreset(preset.value);
                const isActive =
                  filters.from_date === range.from &&
                  filters.to_date === range.to;
                return (
                  <Button
                    key={preset.value}
                    size="small"
                    variant={isActive ? 'contained' : 'outlined'}
                    onClick={() => {
                      const newFilters = {
                        ...filters,
                        from_date: range.from,
                        to_date: range.to,
                        range: preset.value === 'today' ? 'daily' : 'weekly',
                        limit: 100,
                      };
                      setFilters(newFilters);
                      // Clear any pending debounce timer
                      if (debounceTimerRef.current) {
                        clearTimeout(debounceTimerRef.current);
                      }
                      // Call loadData immediately with new filters
                      loadData(newFilters);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      px: 1.5,
                      py: 0.5,
                      minWidth: 'auto',
                      ...(isActive
                        ? {
                            background:
                              'linear-gradient(135deg, #5965A6 0%, #4C5B9A 100%)',
                            color: '#ffffff',
                            '&:hover': {
                              background:
                                'linear-gradient(135deg, #4C5B9A 0%, #3d4a7d 100%)',
                            },
                          }
                        : {
                            borderColor: '#D1D5DB',
                            color: '#6B7280',
                            '&:hover': {
                              borderColor: '#5965A6',
                              backgroundColor: 'rgba(89, 101, 166, 0.08)',
                            },
                          }),
                    }}
                  >
                    {preset.label}
                  </Button>
                );
              })}
            </Box>
            {/* Divider */}
            <Divider sx={{ my: 2, borderColor: '#E5E7EB' }} />

            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <Tooltip
                title="Select the time range for filtering workflow runs: Daily (single day), Weekly (last 7 days), or Monthly (custom range)"
                arrow
                placement="top"
              >
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 120,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      transition: 'all 0.2s ease',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: '#F9FAFB',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#ffffff',
                        boxShadow: '0 0 0 2px rgba(89, 101, 166, 0.1)',
                      },
                    },
                  }}
                >
                  <InputLabel id="range-label">Range</InputLabel>
                  <Select
                    labelId="range-label"
                    value={filters.range}
                    label="Range"
                    onChange={handleRangeChange}
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>

              <Tooltip
                title="Select the start date for filtering workflow runs. For daily range, this will also set the end date automatically"
                arrow
                placement="top"
              >
                <TextField
                  size="small"
                  label="From"
                  type="date"
                  value={filters.from_date}
                  onChange={handleFromDateChange}
                  sx={{
                    minWidth: 155,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      transition: 'all 0.2s ease',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: '#F9FAFB',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#ffffff',
                        boxShadow: '0 0 0 2px rgba(89, 101, 166, 0.1)',
                      },
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Tooltip>

              <Tooltip
                title="Select the end date for filtering workflow runs. For daily range, this will also set the start date automatically"
                arrow
                placement="top"
              >
                <TextField
                  size="small"
                  label="To"
                  type="date"
                  value={filters.to_date}
                  onChange={handleToDateChange}
                  sx={{
                    minWidth: 155,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      transition: 'all 0.2s ease',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: '#F9FAFB',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#ffffff',
                        boxShadow: '0 0 0 2px rgba(89, 101, 166, 0.1)',
                      },
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Tooltip>

              <Tooltip
                title="Filter workflows by execution type: All (both types), Scheduled (automated runs), or On Demand (manual runs)"
                arrow
                placement="top"
              >
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 130,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      transition: 'all 0.2s ease',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: '#F9FAFB',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#ffffff',
                        boxShadow: '0 0 0 2px rgba(89, 101, 166, 0.1)',
                      },
                    },
                  }}
                >
                  <InputLabel id="runmode-label">Run Mode</InputLabel>
                  <Select
                    labelId="runmode-label"
                    value={filters.runMode}
                    label="Run Mode"
                    onChange={handleChange('runMode')}
                  >
                    <MenuItem value="ALL">All</MenuItem>
                    <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                    <MenuItem value="ON DEMAND">On Demand</MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>

              <Tooltip
                title="Filter workflows by record status: All (all records), Success (only workflows with successful records), or Failed (only workflows with failed records)"
                arrow
                placement="top"
              >
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 130,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      transition: 'all 0.2s ease',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: '#F9FAFB',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#ffffff',
                        boxShadow: '0 0 0 2px rgba(89, 101, 166, 0.1)',
                      },
                    },
                  }}
                >
                  <InputLabel id="records-label">Records</InputLabel>
                  <Select
                    labelId="records-label"
                    value={filters.recordType}
                    label="Records"
                    onChange={handleChange('recordType')}
                  >
                    <MenuItem value="ALL">All</MenuItem>
                    <MenuItem value="SUCCESS">Success</MenuItem>
                    <MenuItem value="FAILED">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>

              <Tooltip
                title={
                  limitError ||
                  'Set the maximum number of workflow runs to display (1-100). Maximum limit is 100'
                }
                arrow
                placement="top"
              >
                <TextField
                  size="small"
                  label="Limit"
                  type="number"
                  value={filters.limit}
                  onChange={handleChange('limit')}
                  error={!!limitError}
                  inputProps={{ min: 1, max: 100 }}
                  sx={{
                    width: 90,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      transition: 'all 0.2s ease',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: '#F9FAFB',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#ffffff',
                        boxShadow: '0 0 0 2px rgba(89, 101, 166, 0.1)',
                      },
                      '&.Mui-error': {
                        boxShadow: '0 0 0 2px rgba(239, 68, 68, 0.1)',
                      },
                    },
                  }}
                />
              </Tooltip>

              <Tooltip
                title="Search workflows by name. The search is case-insensitive and matches partial workflow names"
                arrow
                placement="top"
              >
                <TextField
                  size="small"
                  label="Search"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 180,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      transition: 'all 0.2s ease',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: '#F9FAFB',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#ffffff',
                        boxShadow: '0 0 0 2px rgba(89, 101, 166, 0.1)',
                      },
                    },
                  }}
                />
              </Tooltip>

              <Button
                variant="contained"
                onClick={loadData}
                disabled={loading}
                sx={{
                  minWidth: 95,
                  height: 40,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderRadius: '8px',
                  boxShadow: 'none',
                  background:
                    'linear-gradient(135deg, #5965A6 0%, #4C5B9A 100%)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(89, 101, 166, 0.3)',
                    background:
                      'linear-gradient(135deg, #4C5B9A 0%, #3d4a7d 100%)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              >
                {loading ? 'Loading...' : 'Apply'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  const { from, to } = getWeeklyDateRange();
                  setFilters({
                    range: 'weekly',
                    from_date: from,
                    to_date: to,
                    limit: 100,
                    runMode: 'ALL',
                    recordType: 'ALL',
                  });
                  setSearch('');
                  setLimitError('');
                  // Filters will auto-apply via useEffect
                }}
                disabled={loading}
                sx={{
                  minWidth: 80,
                  height: 40,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderRadius: '8px',
                  borderColor: '#D1D5DB',
                  color: '#6B7280',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#9CA3AF',
                    backgroundColor: '#F9FAFB',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                Reset
              </Button>
            </Box>
          </Paper>

          {/* KPI Cards - 8 Cards (Record Metrics + Run Metrics) */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Record Metrics Row */}
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#6B7280',
                  mb: 1,
                  ml: 0.5,
                }}
              >
                Record Metrics
              </Typography>
            </Grid>

            {/* Total Records */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Card
                  sx={{
                    background:
                      'linear-gradient(135deg, #5965A6 0%, #4C5B9A 100%)',
                    color: '#ffffff',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(89, 101, 166, 0.2)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(89, 101, 166, 0.3)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', opacity: 0.9 }}>
                        Total Records
                      </Typography>
                      <Tooltip
                        title="Total number of records processed (success + failure)"
                        arrow
                        placement="top"
                      >
                        <InfoIcon
                          sx={{ fontSize: 14, opacity: 0.8, cursor: 'help' }}
                        />
                      </Tooltip>
                    </Box>
                    <Typography
                      sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 0.25 }}
                    >
                      {kpiData.totalRecords.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                      of all records processed
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Record Success Rate */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Card
                  sx={{
                    background:
                      'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    color: '#ffffff',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(16, 185, 129, 0.3)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', opacity: 0.9 }}>
                        Record Success Rate
                      </Typography>
                      <Tooltip
                        title="Percentage of successfully processed records out of total records"
                        arrow
                        placement="top"
                      >
                        <InfoIcon
                          sx={{ fontSize: 14, opacity: 0.8, cursor: 'help' }}
                        />
                      </Tooltip>
                    </Box>
                    <Typography
                      sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 0.25 }}
                    >
                      {kpiData.recordSuccessRate.toFixed(1)}%
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                      of {kpiData.totalRecords.toLocaleString()} records
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Success Records */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Card
                  sx={{
                    background:
                      'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    color: '#ffffff',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(16, 185, 129, 0.3)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', opacity: 0.9 }}>
                        Success Records
                      </Typography>
                      <CheckCircleIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                    </Box>
                    <Typography
                      sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 0.25 }}
                    >
                      {kpiData.totalSuccessRecords.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                      successfully processed
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Failure Records */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Card
                  sx={{
                    background:
                      'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                    color: '#ffffff',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(239, 68, 68, 0.3)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', opacity: 0.9 }}>
                        Failure Records
                      </Typography>
                      <CancelIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                    </Box>
                    <Typography
                      sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 0.25 }}
                    >
                      {kpiData.totalFailureRecords.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                      failed to process
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Run Metrics Row */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#6B7280',
                  mb: 1,
                  ml: 0.5,
                }}
              >
                Run Metrics
              </Typography>
            </Grid>

            {/* Total Runs */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Card
                  sx={{
                    background:
                      'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                    color: '#ffffff',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(59, 130, 246, 0.3)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', opacity: 0.9 }}>
                        Total Runs
                      </Typography>
                      <Tooltip
                        title="Total number of workflow run instances"
                        arrow
                        placement="top"
                      >
                        <InfoIcon
                          sx={{ fontSize: 14, opacity: 0.8, cursor: 'help' }}
                        />
                      </Tooltip>
                    </Box>
                    <Typography
                      sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 0.25 }}
                    >
                      {kpiData.totalRuns.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                      workflow executions
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Run Success Rate */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Card
                  sx={{
                    background:
                      'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    color: '#ffffff',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(16, 185, 129, 0.3)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', opacity: 0.9 }}>
                        Run Success Rate
                      </Typography>
                      <Tooltip
                        title="Percentage of successful workflow runs (status=SUCCESS with no failures) out of total runs"
                        arrow
                        placement="top"
                      >
                        <InfoIcon
                          sx={{ fontSize: 14, opacity: 0.8, cursor: 'help' }}
                        />
                      </Tooltip>
                    </Box>
                    <Typography
                      sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 0.25 }}
                    >
                      {kpiData.runSuccessRate.toFixed(1)}%
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                      of {kpiData.totalRuns.toLocaleString()} runs
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Scheduled Runs */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Card
                  sx={{
                    background:
                      'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                    color: '#ffffff',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(139, 92, 246, 0.3)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', opacity: 0.9 }}>
                        Scheduled Runs
                      </Typography>
                      <Tooltip
                        title="Number of automated/scheduled workflow runs"
                        arrow
                        placement="top"
                      >
                        <InfoIcon
                          sx={{ fontSize: 14, opacity: 0.8, cursor: 'help' }}
                        />
                      </Tooltip>
                    </Box>
                    <Typography
                      sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 0.25 }}
                    >
                      {kpiData.scheduledRuns.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                      automated executions
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* On-Demand Runs */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Card
                  sx={{
                    background:
                      'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                    color: '#ffffff',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(245, 158, 11, 0.3)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', opacity: 0.9 }}>
                        On-Demand Runs
                      </Typography>
                      <Tooltip
                        title="Number of manually triggered workflow runs"
                        arrow
                        placement="top"
                      >
                        <InfoIcon
                          sx={{ fontSize: 14, opacity: 0.8, cursor: 'help' }}
                        />
                      </Tooltip>
                    </Box>
                    <Typography
                      sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 0.25 }}
                    >
                      {kpiData.onDemandRuns.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                      manual executions
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          {/* Period Comparison - Enhanced with Date Ranges and Both Record/Run Metrics */}
          {periodComparison && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid #E5E7EB',
                borderRadius: 2,
                mb: 2,
                background: 'linear-gradient(to bottom, #ffffff, #F9FAFB)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  Period Comparison
                </Typography>
                <Tooltip
                  title="Compares current selected period with previous equivalent period. Auto-updates when filters change."
                  arrow
                  placement="top"
                >
                  <InfoIcon
                    sx={{ fontSize: 16, color: '#6B7280', cursor: 'help' }}
                  />
                </Tooltip>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: '#F9FAFB',
                      borderRadius: 1,
                      border: '1px solid #E5E7EB',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        color: '#6B7280',
                        mb: 0.5,
                        fontWeight: 600,
                      }}
                    >
                      Current Period
                    </Typography>
                    <Typography
                      sx={{ fontSize: '0.7rem', color: '#9CA3AF', mb: 1.5 }}
                    >
                      {periodComparison.currentPeriod}
                    </Typography>
                    <Grid container spacing={2}>
                      {/* Record Metrics */}
                      <Grid item xs={12}>
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            color: '#6B7280',
                            fontWeight: 600,
                            mb: 1,
                          }}
                        >
                          Record Metrics
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}
                        >
                          Record Success Rate
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            color: '#10B981',
                          }}
                        >
                          {periodComparison.current.recordSuccessRate}%
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.65rem',
                            color: '#9CA3AF',
                            mt: 0.25,
                          }}
                        >
                          {periodComparison.current.successRecords.toLocaleString()}{' '}
                          success
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}
                        >
                          Total Records
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            color: '#111827',
                          }}
                        >
                          {periodComparison.current.totalRecords.toLocaleString()}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.65rem',
                            color: '#9CA3AF',
                            mt: 0.25,
                          }}
                        >
                          {periodComparison.current.failureRecords.toLocaleString()}{' '}
                          failed
                        </Typography>
                      </Grid>
                      {/* Run Metrics */}
                      <Grid item xs={12} sx={{ mt: 1 }}>
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            color: '#6B7280',
                            fontWeight: 600,
                            mb: 1,
                          }}
                        >
                          Run Metrics
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}
                        >
                          Run Success Rate
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            color: '#10B981',
                          }}
                        >
                          {periodComparison.current.runSuccessRate}%
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.65rem',
                            color: '#9CA3AF',
                            mt: 0.25,
                          }}
                        >
                          {periodComparison.current.successfulRuns} successful
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}
                        >
                          Total Runs
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            color: '#111827',
                          }}
                        >
                          {periodComparison.current.totalRuns}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.65rem',
                            color: '#9CA3AF',
                            mt: 0.25,
                          }}
                        >
                          {periodComparison.current.failedRuns} failed
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: '#F9FAFB',
                      borderRadius: 1,
                      border: '1px solid #E5E7EB',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        color: '#6B7280',
                        mb: 0.5,
                        fontWeight: 600,
                      }}
                    >
                      Previous Period
                    </Typography>
                    <Typography
                      sx={{ fontSize: '0.7rem', color: '#9CA3AF', mb: 1.5 }}
                    >
                      {periodComparison.previousPeriod}
                    </Typography>
                    <Grid container spacing={2}>
                      {/* Record Metrics */}
                      <Grid item xs={12}>
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            color: '#6B7280',
                            fontWeight: 600,
                            mb: 1,
                          }}
                        >
                          Record Metrics
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}
                        >
                          Record Success Rate
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            color: '#10B981',
                          }}
                        >
                          {periodComparison.previous.recordSuccessRate}%
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.65rem',
                            color: '#9CA3AF',
                            mt: 0.25,
                          }}
                        >
                          {periodComparison.previous.successRecords.toLocaleString()}{' '}
                          success
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          {periodComparison.changes.recordSuccessRate >= 0 ? (
                            <TrendingUpIcon
                              sx={{ fontSize: 12, color: '#10B981' }}
                            />
                          ) : (
                            <TrendingDownIcon
                              sx={{ fontSize: 12, color: '#EF4444' }}
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: '0.65rem',
                              color:
                                periodComparison.changes.recordSuccessRate >= 0
                                  ? '#10B981'
                                  : '#EF4444',
                              fontWeight: 600,
                            }}
                          >
                            {periodComparison.changes.recordSuccessRate >= 0
                              ? '+'
                              : ''}
                            {periodComparison.changes.recordSuccessRate.toFixed(
                              1,
                            )}
                            %
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}
                        >
                          Total Records
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            color: '#111827',
                          }}
                        >
                          {periodComparison.previous.totalRecords.toLocaleString()}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.65rem',
                            color: '#9CA3AF',
                            mt: 0.25,
                          }}
                        >
                          {periodComparison.previous.failureRecords.toLocaleString()}{' '}
                          failed
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          {periodComparison.changes.totalRecords >= 0 ? (
                            <TrendingUpIcon
                              sx={{ fontSize: 12, color: '#10B981' }}
                            />
                          ) : (
                            <TrendingDownIcon
                              sx={{ fontSize: 12, color: '#EF4444' }}
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: '0.65rem',
                              color:
                                periodComparison.changes.totalRecords >= 0
                                  ? '#10B981'
                                  : '#EF4444',
                              fontWeight: 600,
                            }}
                          >
                            {periodComparison.changes.totalRecords >= 0
                              ? '+'
                              : ''}
                            {periodComparison.changes.totalRecords.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Grid>
                      {/* Run Metrics */}
                      <Grid item xs={12} sx={{ mt: 1 }}>
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            color: '#6B7280',
                            fontWeight: 600,
                            mb: 1,
                          }}
                        >
                          Run Metrics
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}
                        >
                          Run Success Rate
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            color: '#10B981',
                          }}
                        >
                          {periodComparison.previous.runSuccessRate}%
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.65rem',
                            color: '#9CA3AF',
                            mt: 0.25,
                          }}
                        >
                          {periodComparison.previous.successfulRuns} successful
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          {periodComparison.changes.runSuccessRate >= 0 ? (
                            <TrendingUpIcon
                              sx={{ fontSize: 12, color: '#10B981' }}
                            />
                          ) : (
                            <TrendingDownIcon
                              sx={{ fontSize: 12, color: '#EF4444' }}
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: '0.65rem',
                              color:
                                periodComparison.changes.runSuccessRate >= 0
                                  ? '#10B981'
                                  : '#EF4444',
                              fontWeight: 600,
                            }}
                          >
                            {periodComparison.changes.runSuccessRate >= 0
                              ? '+'
                              : ''}
                            {periodComparison.changes.runSuccessRate.toFixed(1)}
                            %
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}
                        >
                          Total Runs
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            color: '#111827',
                          }}
                        >
                          {periodComparison.previous.totalRuns}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.65rem',
                            color: '#9CA3AF',
                            mt: 0.25,
                          }}
                        >
                          {periodComparison.previous.failedRuns} failed
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          {periodComparison.changes.totalRuns >= 0 ? (
                            <TrendingUpIcon
                              sx={{ fontSize: 12, color: '#10B981' }}
                            />
                          ) : (
                            <TrendingDownIcon
                              sx={{ fontSize: 12, color: '#EF4444' }}
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: '0.65rem',
                              color:
                                periodComparison.changes.totalRuns >= 0
                                  ? '#10B981'
                                  : '#EF4444',
                              fontWeight: 600,
                            }}
                          >
                            {periodComparison.changes.totalRuns >= 0 ? '+' : ''}
                            {periodComparison.changes.totalRuns.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Anomaly Alerts */}
          {anomalies.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Alert
                severity="warning"
                icon={<WarningIcon />}
                sx={{
                  mb: 2,
                  borderRadius: '8px',
                  backgroundColor: '#FEF3C7',
                  border: '1px solid #FCD34D',
                  '& .MuiAlert-icon': {
                    color: '#F59E0B',
                  },
                  '& .MuiAlert-message': {
                    color: '#92400E',
                  },
                }}
              >
                <AlertTitle sx={{ fontWeight: 600, color: '#92400E', mb: 1 }}>
                  Anomalies Detected ({anomalies.length})
                </AlertTitle>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                >
                  {anomalies.slice(0, 3).map((anomaly, idx) => (
                    <Typography key={idx} sx={{ fontSize: '0.85rem' }}>
                      • {anomaly.message} -{' '}
                      <strong>{anomaly.workflowName}</strong>
                    </Typography>
                  ))}
                  {anomalies.length > 3 && (
                    <Typography
                      sx={{ fontSize: '0.85rem', fontStyle: 'italic' }}
                    >
                      ...and {anomalies.length - 3} more
                    </Typography>
                  )}
                </Box>
              </Alert>
            </motion.div>
          )}

          {/* Performance Percentiles */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              border: '1px solid #E5E7EB',
              borderRadius: 2,
              mb: 2,
              background: 'linear-gradient(to bottom, #ffffff, #F9FAFB)',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.95rem',
                fontWeight: 600,
                color: '#111827',
                mb: 2,
              }}
            >
              Performance Percentiles (Execution Time)
            </Typography>
            <Grid container spacing={2}>
              {[
                {
                  label: 'P50 (Median)',
                  value: performancePercentiles.p50,
                  color: '#3B82F6',
                },
                {
                  label: 'P75',
                  value: performancePercentiles.p75,
                  color: '#8B5CF6',
                },
                {
                  label: 'P95',
                  value: performancePercentiles.p95,
                  color: '#F59E0B',
                },
                {
                  label: 'P99',
                  value: performancePercentiles.p99,
                  color: '#EF4444',
                },
              ].map((percentile, idx) => (
                <Grid item xs={12} sm={6} md={3} key={idx}>
                  <motion.div variants={itemVariants}>
                    <Card
                      sx={{
                        background: `linear-gradient(135deg, ${percentile.color} 0%, ${percentile.color}dd 100%)`,
                        color: '#ffffff',
                        borderRadius: 2,
                        boxShadow: `0 4px 12px ${percentile.color}33`,
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 6px 16px ${percentile.color}44`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Typography
                          sx={{ fontSize: '0.75rem', opacity: 0.9, mb: 0.5 }}
                        >
                          {percentile.label}
                        </Typography>
                        <Typography
                          sx={{ fontSize: '1.5rem', fontWeight: 700 }}
                        >
                          {formatExecutionTime(percentile.value)}
                        </Typography>
                        <Typography
                          sx={{ fontSize: '0.65rem', opacity: 0.8, mt: 0.5 }}
                        >
                          {percentile.value > 0
                            ? '50% of runs complete faster'
                            : 'No data'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Top 5 Workflows and Top 5 Connections */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Top 5 Workflows */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #E5E7EB',
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: '#111827',
                    mb: 2,
                  }}
                >
                  Top 5 Workflows by Total Records
                </Typography>
                {topWorkflows.length > 0 ? (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                  >
                    {topWorkflows.map((wf, idx) => (
                      <motion.div
                        key={wf.workflowId}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                      >
                        <Card
                          sx={{
                            p: 1.5,
                            border: '1px solid #E5E7EB',
                            borderRadius: 1,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: '#5965A6',
                              backgroundColor: '#F9FAFB',
                              transform: 'translateX(4px)',
                            },
                          }}
                          onClick={() => {
                            setSearch(wf.workflowName);
                            setSelectedWorkflowName(wf.workflowName);
                            setShowWorkflowToast(true);
                            setTimeout(() => {
                              document
                                .querySelector('[data-workflow-table]')
                                ?.scrollIntoView({ behavior: 'smooth' });
                            }, 100);
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#111827',
                              }}
                            >
                              {idx + 1}. {wf.workflowName}
                            </Typography>
                            <Chip
                              label={`${wf.totalRecords.toLocaleString()}`}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                backgroundColor: '#E0E7FF',
                                color: '#4C5B9A',
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                            <Box>
                              <Typography
                                sx={{ fontSize: '0.7rem', color: '#6B7280' }}
                              >
                                Record Success Rate
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.85rem',
                                  fontWeight: 600,
                                  color: '#10B981',
                                }}
                              >
                                {wf.recordSuccessRate.toFixed(1)}%
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                sx={{ fontSize: '0.7rem', color: '#6B7280' }}
                              >
                                Total Runs
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.85rem',
                                  fontWeight: 600,
                                  color: '#111827',
                                }}
                              >
                                {wf.totalRuns}
                              </Typography>
                            </Box>
                          </Box>
                        </Card>
                      </motion.div>
                    ))}
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      color: '#9CA3AF',
                      fontSize: '0.85rem',
                      textAlign: 'center',
                      py: 4,
                    }}
                  >
                    No workflow data available
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Top 5 Source Connections */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #E5E7EB',
                  borderRadius: 2,
                  height: '100%',
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: '#111827',
                    mb: 2,
                  }}
                >
                  Top 5 Source Connections by Total Records
                </Typography>
                {topSourceConnections.length > 0 ? (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                  >
                    {topSourceConnections.map((conn, idx) => (
                      <motion.div
                        key={conn.connectionName}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                      >
                        <Card
                          sx={{
                            p: 1.5,
                            border: '1px solid #E5E7EB',
                            borderRadius: 1,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: '#5965A6',
                              backgroundColor: '#F9FAFB',
                              transform: 'translateX(4px)',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#111827',
                              }}
                            >
                              {idx + 1}. {conn.connectionName}
                            </Typography>
                            <Chip
                              label={`${conn.totalRecords.toLocaleString()}`}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                backgroundColor: '#E0E7FF',
                                color: '#4C5B9A',
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                            <Box>
                              <Typography
                                sx={{ fontSize: '0.7rem', color: '#6B7280' }}
                              >
                                Record Success Rate
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.85rem',
                                  fontWeight: 600,
                                  color: '#10B981',
                                }}
                              >
                                {conn.recordSuccessRate.toFixed(1)}%
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                sx={{ fontSize: '0.7rem', color: '#6B7280' }}
                              >
                                Workflows
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.85rem',
                                  fontWeight: 600,
                                  color: '#111827',
                                }}
                              >
                                {conn.workflowCount}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                sx={{ fontSize: '0.7rem', color: '#6B7280' }}
                              >
                                Total Runs
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.85rem',
                                  fontWeight: 600,
                                  color: '#111827',
                                }}
                              >
                                {conn.totalRuns}
                              </Typography>
                            </Box>
                          </Box>
                        </Card>
                      </motion.div>
                    ))}
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      color: '#9CA3AF',
                      fontSize: '0.85rem',
                      textAlign: 'center',
                      py: 4,
                    }}
                  >
                    No connection data available
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Hourly/Daily Performance Heatmap */}
          {performanceHeatmap.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid #E5E7EB',
                borderRadius: 2,
                mt: 5,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  color: '#111827',
                  mb: 2,
                }}
              >
                Performance Heatmap (Hour × Day of Week)
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ display: 'inline-block', minWidth: '100%' }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '80px repeat(24, 1fr)',
                      gap: 0.5,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#6B7280',
                      }}
                    >
                      Day/Hour
                    </Box>
                    {Array.from({ length: 24 }, (_, i) => (
                      <Box
                        key={i}
                        sx={{
                          p: 1,
                          fontSize: '0.7rem',
                          textAlign: 'center',
                          color: '#6B7280',
                          fontWeight: 600,
                        }}
                      >
                        {i}h
                      </Box>
                    ))}
                  </Box>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                    (day) => {
                      const dayData = performanceHeatmap.filter(
                        (d) => d.day === day,
                      );
                      return (
                        <Box
                          key={day}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '80px repeat(24, 1fr)',
                            gap: 0.5,
                            mb: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              p: 1,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: '#111827',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {day}
                          </Box>
                          {Array.from({ length: 24 }, (_, hour) => {
                            const hourData = dayData.find(
                              (d) => d.hour === hour,
                            );
                            const successRate = hourData?.successRate || 0;
                            const intensity = Math.min(100, successRate);
                            const color =
                              intensity >= 90
                                ? '#10B981'
                                : intensity >= 70
                                  ? '#F59E0B'
                                  : intensity >= 50
                                    ? '#EF4444'
                                    : '#E5E7EB';
                            const opacity = hourData
                              ? Math.max(0.3, intensity / 100)
                              : 0.1;
                            return (
                              <Tooltip
                                key={hour}
                                title={
                                  hourData
                                    ? `${day} ${hour}:00 - Success Rate: ${successRate.toFixed(1)}% (${hourData.total.toLocaleString()} records)`
                                    : `${day} ${hour}:00 - No data`
                                }
                                arrow
                              >
                                <Box
                                  sx={{
                                    p: 1,
                                    backgroundColor: `${color}${Math.round(
                                      opacity * 255,
                                    )
                                      .toString(16)
                                      .padStart(2, '0')}`,
                                    border: `1px solid ${color}40`,
                                    borderRadius: 1,
                                    cursor: hourData ? 'pointer' : 'default',
                                    transition: 'all 0.2s',
                                    '&:hover': hourData
                                      ? {
                                          transform: 'scale(1.1)',
                                          zIndex: 1,
                                          boxShadow: `0 2px 8px ${color}66`,
                                        }
                                      : {},
                                  }}
                                />
                              </Tooltip>
                            );
                          })}
                        </Box>
                      );
                    },
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  fontSize: '0.75rem',
                  color: '#6B7280',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: '#10B98180',
                      borderRadius: 1,
                      border: '1px solid #10B98140',
                    }}
                  />
                  <Typography sx={{ fontSize: '0.7rem' }}>
                    High Success (≥90%)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: '#F59E0B80',
                      borderRadius: 1,
                      border: '1px solid #F59E0B40',
                    }}
                  />
                  <Typography sx={{ fontSize: '0.7rem' }}>
                    Medium (70-89%)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: '#EF444480',
                      borderRadius: 1,
                      border: '1px solid #EF444440',
                    }}
                  />
                  <Typography sx={{ fontSize: '0.7rem' }}>
                    Low (50-69%)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: '#E5E7EB80',
                      borderRadius: 1,
                      border: '1px solid #E5E7EB40',
                    }}
                  />
                  <Typography sx={{ fontSize: '0.7rem' }}>
                    Very Low (&lt;50%)
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Time-Series Chart */}
          {timeSeriesData.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid #E5E7EB',
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  color: '#111827',
                  mb: 2,
                }}
              >
                Trends Over Time
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={timeSeriesData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="colorSuccess"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#10B981"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#10B981"
                          stopOpacity={0}
                        />
                      </linearGradient>
                      <linearGradient
                        id="colorFailure"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#EF4444"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#EF4444"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <RechartsTooltip
                      formatter={(value, name) => [
                        Number(value).toLocaleString(),
                        name,
                      ]}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="success"
                      stroke="#10B981"
                      fillOpacity={1}
                      fill="url(#colorSuccess)"
                      name="Success"
                    />
                    <Area
                      type="monotone"
                      dataKey="failure"
                      stroke="#EF4444"
                      fillOpacity={1}
                      fill="url(#colorFailure)"
                      name="Failure"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          )}

          {/* Status Distribution and Workflow Breakdown Side by Side */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Status Distribution */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #E5E7EB',
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: '#111827',
                    mb: 2,
                  }}
                >
                  Status Distribution
                </Typography>
                {statusDistribution.length > 0 ? (
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={statusDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percentage }) =>
                            `${name}: ${percentage}%`
                          }
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {statusDistribution.map((entry, index) => {
                            const status = entry.name
                              .toUpperCase()
                              .replace(' ', '_');
                            const color =
                              statusColorMap[status]?.bg || '#A3A3A3';
                            return <Cell key={`cell-${index}`} fill={color} />;
                          })}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      color: '#9CA3AF',
                      fontSize: '0.85rem',
                      textAlign: 'center',
                      py: 4,
                    }}
                  >
                    No status data available
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Workflow Breakdown (Recharts horizontal grouped bars) */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      color: '#111827',
                    }}
                  >
                    Workflow Breakdown
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
                    Total Records: Success{' '}
                    {workflowAgg.rows
                      .reduce((sum, row) => sum + row.success, 0)
                      .toLocaleString()}{' '}
                    | Failed{' '}
                    {workflowAgg.rows
                      .reduce((sum, row) => sum + row.failure, 0)
                      .toLocaleString()}
                  </Typography>
                </Box>

                {workflowAgg.rows.length === 0 ? (
                  <Typography
                    sx={{
                      color: '#9CA3AF',
                      fontSize: '0.85rem',
                      textAlign: 'center',
                      py: 4,
                    }}
                  >
                    No workflow data for current filters.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      height: Math.min(
                        300,
                        48 * Math.min(filters.limit, workflowAgg.rows.length),
                      ),
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        layout="vertical"
                        data={workflowAgg.rows.slice(0, filters.limit)}
                        margin={{ top: 8, right: 16, bottom: 8, left: 0 }}
                      >
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={180}
                          tick={<WorkflowYAxisTick />}
                        />
                        <XAxis type="number" tick={{ fontSize: 12 }} />
                        <RechartsTooltip
                          formatter={(value, name) => [
                            Number(value).toLocaleString(),
                            name,
                          ]}
                        />
                        <Legend />
                        <Bar
                          dataKey="success"
                          name="success"
                          fill="#10B981"
                          onClick={(data) => {
                            if (data && data.workflow_id) {
                              setSearch(data.name);
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                        <Bar
                          dataKey="failure"
                          name="failed"
                          fill="#EF4444"
                          onClick={(data) => {
                            if (data && data.workflow_id) {
                              setSearch(data.name);
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          <WorkflowConnectionsDialog
            open={connOpen}
            onClose={() => setConnOpen(false)}
            loading={connLoading}
            error={connError}
            data={connData}
            workflowName={connWorkflowName}
          />

          {/* Table */}
          <motion.div variants={itemVariants}>
            <Box
              component={motion.div}
              sx={{
                borderRadius: '12px',
                background: '#ffffff',
                border: '1px solid #E5E7EB',
                p: 0,
                mt: 1,
                mb: 2,
                position: 'relative',
                overflow: 'hidden',
                boxShadow:
                  '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              }}
            >
              {/* Table Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  borderBottom: '1px solid #E5E7EB',
                  backgroundColor: '#ffffff',
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: '#111827',
                      mb: 0,
                    }}
                  >
                    Workflow Run History
                  </Typography>
                </Box>
                <Tooltip title="Download as CSV" arrow placement="top">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadCSV}
                    disabled={sortedRows.length === 0}
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: '#5965A6',
                      borderColor: 'rgba(89, 101, 166, 0.3)',
                      textTransform: 'none',
                      px: 2,
                      py: 0.75,
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#5965A6',
                        backgroundColor: 'rgba(89, 101, 166, 0.08)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 8px rgba(89, 101, 166, 0.2)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                      '&.Mui-disabled': {
                        color: '#9CA3AF',
                        borderColor: '#E5E7EB',
                      },
                    }}
                  >
                    Download
                  </Button>
                </Tooltip>
              </Box>

              <TableContainer
                sx={{ backgroundColor: '#ffffff', overflow: 'hidden' }}
              >
                <Table
                  size="small"
                  sx={{
                    width: '100%',
                    '& .MuiTableCell-root': {
                      py: 0.5,
                      px: 2,
                      borderBottom: '1px solid #e5e7eb',
                    },
                    '& thead .MuiTableCell-root': { py: 0.5, px: 1.25 },
                    '& .MuiTableRow-root': { height: 44 },
                  }}
                >
                  <TableHead sx={{ backgroundColor: '#5965A6' }}>
                    <TableRow
                      sx={{
                        '&:hover': {
                          backgroundColor: '#4C5B9A',
                        },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          py: 1,
                          px: 2,
                          color: '#ffffff',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '1px solid #E5E7EB',
                          width: '60px',
                        }}
                      >
                        Sr. No
                      </TableCell>

                      {/* Sortable headers */}
                      {[
                        {
                          label: 'Workflow Name',
                          key: 'workflow_name',
                          sortable: true,
                          align: 'left',
                        },
                        {
                          label: 'Run Count',
                          key: 'run_count',
                          sortable: false,
                          align: 'center',
                          width: '100px',
                        },
                        {
                          label: 'Status',
                          key: 'status',
                          sortable: true,
                          align: 'left',
                        },
                        {
                          label: 'Run Mode',
                          key: 'run_mode',
                          sortable: true,
                          align: 'left',
                        },
                        {
                          label: 'Date',
                          key: 'date',
                          sortable: true,
                          align: 'left',
                        },
                        {
                          label: 'Source',
                          key: 'source',
                          sortable: true,
                          align: 'left',
                        },
                        {
                          label: 'Destination',
                          key: 'destination',
                          sortable: true,
                          align: 'left',
                        },
                        {
                          label: 'Success',
                          key: 'success',
                          sortable: true,
                          align: 'center',
                        },
                        {
                          label: 'Failure',
                          key: 'failure',
                          sortable: true,
                          align: 'center',
                        },
                        {
                          label: 'Execution Time',
                          key: 'execution_time',
                          sortable: true,
                          align: 'center',
                        },
                        {
                          label: 'Throughput',
                          key: 'throughput',
                          sortable: true,
                          align: 'center',
                        },
                        {
                          label: 'Health Score',
                          key: 'health_score',
                          sortable: true,
                          align: 'center',
                        },
                      ].map((header) => (
                        <TableCell
                          key={header.key}
                          align={header.align}
                          onClick={
                            header.sortable
                              ? () => handleSort(header.key)
                              : undefined
                          }
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            py: 1,
                            px: 2,
                            color: '#ffffff',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid #E5E7EB',
                            width: header.width || 'auto',
                            cursor: header.sortable ? 'pointer' : 'default',
                            userSelect: 'none',
                            position: 'relative',
                            '&:hover': header.sortable
                              ? {
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  transition: 'background-color 0.2s ease',
                                }
                              : {},
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent:
                                header.align === 'center'
                                  ? 'center'
                                  : 'space-between',
                            }}
                          >
                            {header.label}
                            {header.sortable &&
                              sortConfig.column === header.key &&
                              sortConfig.direction === 'asc' && (
                                <ArrowUpwardIcon
                                  sx={{ fontSize: '0.875rem', ml: 0.5 }}
                                />
                              )}
                            {header.sortable &&
                              sortConfig.column === header.key &&
                              sortConfig.direction === 'desc' && (
                                <ArrowDownwardIcon
                                  sx={{ fontSize: '0.875rem', ml: 0.5 }}
                                />
                              )}
                            {header.sortable &&
                              (!sortConfig.column ||
                                sortConfig.column !== header.key) && (
                                <UnfoldMoreIcon
                                  sx={{
                                    fontSize: '0.875rem',
                                    ml: 0.5,
                                    opacity: 0.5,
                                  }}
                                />
                              )}
                          </Box>
                        </TableCell>
                      ))}

                      {/* Logs header - not sortable */}
                      <TableCell
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          py: 1,
                          px: 2,
                          color: '#ffffff',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        Logs
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: 6 }).map((_, i) => (
                        <TableRow
                          key={`sk-${i}`}
                          sx={{ borderBottom: '1px solid #F3F4F6' }}
                        >
                          {Array.from({ length: 11 }).map((__, j) => (
                            <TableCell key={`skc-${i}-${j}`}>
                              <Skeleton
                                variant="text"
                                width={
                                  j === 0
                                    ? 20
                                    : j === 1
                                      ? 110
                                      : j === 2
                                        ? 60
                                        : j === 3
                                          ? 80
                                          : 100
                                }
                                height={20}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : groupedWorkflows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={14}
                          align="center"
                          sx={{ py: 8, border: 'none' }}
                        >
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                          >
                            <Typography
                              sx={{
                                color: '#9CA3AF',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                              }}
                            >
                              {error || 'No records found for current filters.'}
                            </Typography>
                          </motion.div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <AnimatePresence>
                        {groupedWorkflows.map((group, groupIdx) => {
                          const groupKey = `${group.workflow_name}_${group.workflow_id}`;
                          const isExpanded = expandedGroups.has(groupKey);
                          const hasMultipleRuns = group.runCount > 1;

                          // Use the most recent run (first in array) for parent row data
                          const latestRun = group.runs[0];
                          const runMode = latestRun?.is_scheduled
                            ? 'SCHEDULED'
                            : 'ON DEMAND';
                          const startDate = latestRun.start_time
                            ? formatLocalDate(new Date(latestRun.start_time))
                            : '-';
                          const { source, destination } = getConnectionNames(
                            group.workflow_id,
                          );

                          // Calculate total success and failure counts for the group
                          const totalSuccessCount = group.runs.reduce(
                            (sum, run) =>
                              sum + (Number(run.success_record_count) || 0),
                            0,
                          );
                          const totalFailureCount = group.runs.reduce(
                            (sum, run) =>
                              sum + (Number(run.failure_record_count) || 0),
                            0,
                          );

                          return (
                            <React.Fragment key={groupKey}>
                              {/* Parent/Main Row */}
                              <TableRow
                                component={motion.tr}
                                custom={groupIdx}
                                variants={rowVariants}
                                initial="hidden"
                                animate="visible"
                                whileHover="hover"
                                sx={{
                                  borderBottom: '1px solid #F3F4F6',
                                  backgroundColor:
                                    groupIdx % 2 === 1 ? '#F3F5F7' : '#ffffff',
                                  '&:hover': {
                                    backgroundColor:
                                      groupIdx % 2 === 1
                                        ? '#71B9FF40'
                                        : '#71B9FF40',
                                  },
                                }}
                              >
                                <TableCell
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: '#6B7280',
                                  }}
                                >
                                  {groupIdx + 1}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: '#111827',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                    }}
                                  >
                                    {hasMultipleRuns && (
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          toggleGroup(
                                            group.workflow_name,
                                            group.workflow_id,
                                          )
                                        }
                                        sx={{
                                          padding: '2px',
                                          color: '#5965A6',
                                          '&:hover': {
                                            backgroundColor:
                                              'rgba(89, 101, 166, 0.08)',
                                          },
                                        }}
                                      >
                                        {isExpanded ? (
                                          <KeyboardArrowUpIcon
                                            sx={{ fontSize: 18 }}
                                          />
                                        ) : (
                                          <KeyboardArrowDownIcon
                                            sx={{ fontSize: 18 }}
                                          />
                                        )}
                                      </IconButton>
                                    )}
                                    <Link
                                      component={motion.a}
                                      sx={{
                                        color: '#4C5B9A',
                                        textDecoration: 'none',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        display: 'inline-block',
                                        transformOrigin: 'left center',
                                        willChange: 'transform, color',
                                        backfaceVisibility: 'hidden',
                                        WebkitFontSmoothing: 'antialiased',
                                        MozOsxFontSmoothing: 'grayscale',
                                        textRendering: 'optimizeLegibility',
                                        textShadow: '0 0 0 currentColor',
                                      }}
                                      style={{
                                        transformPerspective: 1000,
                                      }}
                                      whileHover={{
                                        scaleX: 1.07,
                                        color: '#4C5B9A',
                                      }}
                                      whileTap={{ scaleX: 1.04 }}
                                      transition={{
                                        duration: 0.18,
                                        ease: 'easeOut',
                                      }}
                                      onClick={() =>
                                        openConnectionsById(
                                          group.workflow_id,
                                          group.workflow_name,
                                        )
                                      }
                                    >
                                      {group.workflow_name}
                                    </Link>
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={group.runCount}
                                    size="small"
                                    sx={{
                                      fontSize: '0.75rem',
                                      fontWeight: 700,
                                      borderRadius: '12px',
                                      height: 24,
                                      minWidth: 40,
                                      backgroundColor: hasMultipleRuns
                                        ? '#E0E7FF'
                                        : '#F3F4F6',
                                      color: hasMultipleRuns
                                        ? '#4C5B9A'
                                        : '#6B7280',
                                      border: hasMultipleRuns
                                        ? '1px solid #C7D2FE'
                                        : '1px solid #D1D5DB',
                                      '& .MuiChip-label': {
                                        px: 1.5,
                                        py: 0.25,
                                      },
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={
                                      (latestRun.status ||
                                        latestRun.run_status) === 'ACTIVE'
                                        ? 'RUNNING'
                                        : latestRun.status ||
                                          latestRun.run_status ||
                                          'N/A'
                                    }
                                    size="small"
                                    sx={{
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      borderRadius: '16px',
                                      height: 28,
                                      minWidth: 80,
                                      backgroundColor:
                                        statusColorMap[
                                          latestRun.status ||
                                            latestRun.run_status
                                        ]?.bgLight || '#F3F4F6',
                                      color:
                                        statusColorMap[
                                          latestRun.status ||
                                            latestRun.run_status
                                        ]?.text || '#6B7280',
                                      border: `1px solid ${
                                        statusColorMap[
                                          latestRun.status ||
                                            latestRun.run_status
                                        ]?.border || '#D1D5DB'
                                      }`,
                                      textTransform: 'capitalize',
                                      '& .MuiChip-label': {
                                        px: 2.5,
                                        py: 0.25,
                                      },
                                    }}
                                  />
                                </TableCell>
                                <TableCell
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    color: '#6b7280',
                                    fontWeight: 400,
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'inline-block',
                                      px: 2,
                                      py: 0.2,
                                      borderRadius: '8px',
                                      fontSize: '0.75rem',
                                      fontWeight: 700,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.025em',
                                      color: '#5965A6',
                                    }}
                                  >
                                    {runMode}
                                  </Box>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    color: '#6B7280',
                                    fontWeight: 400,
                                  }}
                                >
                                  {startDate}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    color: '#6B7280',
                                    fontWeight: 400,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 0.5,
                                    }}
                                  >
                                    {source.map((name, idx) => (
                                      <Typography
                                        key={idx}
                                        sx={{
                                          fontSize: '0.875rem',
                                          color: '#6B7280',
                                        }}
                                      >
                                        {name}
                                      </Typography>
                                    ))}
                                  </Box>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    color: '#6B7280',
                                    fontWeight: 400,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 0.5,
                                    }}
                                  >
                                    {destination.map((name, idx) => (
                                      <Typography
                                        key={idx}
                                        sx={{
                                          fontSize: '0.875rem',
                                          color: '#6B7280',
                                        }}
                                      >
                                        {name}
                                      </Typography>
                                    ))}
                                  </Box>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: statusColorMap.SUCCESS.text,
                                  }}
                                >
                                  {totalSuccessCount.toLocaleString()}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: statusColorMap.FAILED.text,
                                  }}
                                >
                                  {totalFailureCount.toLocaleString()}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    color: '#6B7280',
                                    fontWeight: 400,
                                  }}
                                >
                                  {(() => {
                                    const execTimes = group.runs
                                      .map((r) =>
                                        calculateExecutionTime(
                                          r.start_time,
                                          r.end_time || r.metadata?.end_time,
                                        ),
                                      )
                                      .filter((t) => t !== null && t > 0);
                                    const avgTime =
                                      execTimes.length > 0
                                        ? execTimes.reduce(
                                            (sum, t) => sum + t,
                                            0,
                                          ) / execTimes.length
                                        : null;
                                    return formatExecutionTime(avgTime);
                                  })()}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    color: '#6B7280',
                                    fontWeight: 400,
                                  }}
                                >
                                  {(() => {
                                    const execTimes = group.runs
                                      .map((r) =>
                                        calculateExecutionTime(
                                          r.start_time,
                                          r.end_time || r.metadata?.end_time,
                                        ),
                                      )
                                      .filter((t) => t !== null && t > 0);
                                    const avgTime =
                                      execTimes.length > 0
                                        ? execTimes.reduce(
                                            (sum, t) => sum + t,
                                            0,
                                          ) / execTimes.length
                                        : null;
                                    const throughput = calculateThroughput(
                                      totalSuccessCount,
                                      totalFailureCount,
                                      avgTime,
                                    );
                                    return throughput > 0
                                      ? `${throughput.toFixed(1)}/s`
                                      : '-';
                                  })()}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                  }}
                                >
                                  {(() => {
                                    const healthScore = calculateHealthScore(
                                      totalSuccessCount,
                                      totalFailureCount,
                                    );
                                    const color =
                                      getHealthScoreColor(healthScore);
                                    return (
                                      <Chip
                                        label={`${Math.round(healthScore)}`}
                                        size="small"
                                        sx={{
                                          backgroundColor: `${color}15`,
                                          color: color,
                                          border: `1px solid ${color}40`,
                                          fontWeight: 700,
                                          fontSize: '0.75rem',
                                          height: 24,
                                          minWidth: 50,
                                        }}
                                      />
                                    );
                                  })()}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    py: 0.25,
                                    px: 2,
                                  }}
                                >
                                  <Tooltip
                                    title="View Logs"
                                    arrow
                                    placement="top"
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleViewLogs(
                                          latestRun.workflow_run_id,
                                        )
                                      }
                                      sx={{
                                        color: '#5965A6',
                                        '&:hover': {
                                          backgroundColor:
                                            'rgba(89, 101, 166, 0.08)',
                                        },
                                      }}
                                    >
                                      <Visibility sx={{ fontSize: 20 }} />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>

                              {/* Child Rows (Expanded Individual Runs) */}
                              {hasMultipleRuns &&
                                isExpanded &&
                                group.runs.map((run, runIdx) => {
                                  const childRunMode = run?.is_scheduled
                                    ? 'SCHEDULED'
                                    : 'ON DEMAND';
                                  const childStartDate = run.start_time
                                    ? formatLocalDate(new Date(run.start_time))
                                    : '-';
                                  const {
                                    source: childSource,
                                    destination: childDestination,
                                  } = getConnectionNames(group.workflow_id);

                                  return (
                                    <TableRow
                                      key={
                                        run.workflow_run_id ||
                                        `${groupKey}-${runIdx}`
                                      }
                                      component={motion.tr}
                                      initial={{ opacity: 0, height: 0 }}
                                      animate={{ opacity: 1, height: 'auto' }}
                                      exit={{ opacity: 0, height: 0 }}
                                      transition={{ duration: 0.2 }}
                                      sx={{
                                        borderBottom: '1px solid #F3F4F6',
                                        backgroundColor: '#FAFBFC',
                                        '&:hover': {
                                          backgroundColor: '#F0F4F8',
                                        },
                                      }}
                                    >
                                      <TableCell
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          fontWeight: 400,
                                          color: '#9CA3AF',
                                          paddingLeft: 4,
                                        }}
                                      >
                                        {groupIdx + 1}.{runIdx + 1}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          fontWeight: 400,
                                          color: '#6B7280',
                                          paddingLeft: 6,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: '0.8rem',
                                            color: '#6B7280',
                                            fontStyle: 'italic',
                                          }}
                                        >
                                          Run #{runIdx + 1}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Typography
                                          sx={{
                                            fontSize: '0.75rem',
                                            color: '#9CA3AF',
                                          }}
                                        >
                                          -
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={
                                            (run.status || run.run_status) ===
                                            'ACTIVE'
                                              ? 'RUNNING'
                                              : run.status ||
                                                run.run_status ||
                                                'N/A'
                                          }
                                          size="small"
                                          sx={{
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            borderRadius: '14px',
                                            height: 24,
                                            minWidth: 70,
                                            backgroundColor:
                                              statusColorMap[
                                                run.status || run.run_status
                                              ]?.bgLight || '#F3F4F6',
                                            color:
                                              statusColorMap[
                                                run.status || run.run_status
                                              ]?.text || '#6B7280',
                                            border: `1px solid ${
                                              statusColorMap[
                                                run.status || run.run_status
                                              ]?.border || '#D1D5DB'
                                            }`,
                                            textTransform: 'capitalize',
                                            '& .MuiChip-label': {
                                              px: 2,
                                              py: 0.25,
                                            },
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          color: '#6b7280',
                                          fontWeight: 400,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'inline-block',
                                            px: 1.5,
                                            py: 0.2,
                                            borderRadius: '6px',
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.025em',
                                            color: '#5965A6',
                                          }}
                                        >
                                          {childRunMode}
                                        </Box>
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          color: '#6B7280',
                                          fontWeight: 400,
                                        }}
                                      >
                                        {childStartDate}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          color: '#6B7280',
                                          fontWeight: 400,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                          }}
                                        >
                                          {childSource.map((name, idx) => (
                                            <Typography
                                              key={idx}
                                              sx={{
                                                fontSize: '0.8rem',
                                                color: '#6B7280',
                                              }}
                                            >
                                              {name}
                                            </Typography>
                                          ))}
                                        </Box>
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          color: '#6B7280',
                                          fontWeight: 400,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                          }}
                                        >
                                          {childDestination.map((name, idx) => (
                                            <Typography
                                              key={idx}
                                              sx={{
                                                fontSize: '0.8rem',
                                                color: '#6B7280',
                                              }}
                                            >
                                              {name}
                                            </Typography>
                                          ))}
                                        </Box>
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          fontWeight: 600,
                                          color: statusColorMap.SUCCESS.text,
                                        }}
                                      >
                                        {(
                                          run.success_record_count ?? 0
                                        ).toLocaleString()}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          fontWeight: 600,
                                          color: statusColorMap.FAILED.text,
                                        }}
                                      >
                                        {(
                                          run.failure_record_count ?? 0
                                        ).toLocaleString()}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          color: '#6B7280',
                                          fontWeight: 400,
                                        }}
                                      >
                                        {formatExecutionTime(
                                          calculateExecutionTime(
                                            run.start_time,
                                            run.end_time ||
                                              run.metadata?.end_time,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          color: '#6B7280',
                                          fontWeight: 400,
                                        }}
                                      >
                                        {(() => {
                                          const execTime =
                                            calculateExecutionTime(
                                              run.start_time,
                                              run.end_time ||
                                                run.metadata?.end_time,
                                            );
                                          const throughput =
                                            calculateThroughput(
                                              run.success_record_count || 0,
                                              run.failure_record_count || 0,
                                              execTime,
                                            );
                                          return throughput > 0
                                            ? `${throughput.toFixed(1)}/s`
                                            : '-';
                                        })()}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                          fontSize: '0.8rem',
                                          fontWeight: 600,
                                        }}
                                      >
                                        {(() => {
                                          const healthScore =
                                            calculateHealthScore(
                                              run.success_record_count || 0,
                                              run.failure_record_count || 0,
                                            );
                                          const color =
                                            getHealthScoreColor(healthScore);
                                          return (
                                            <Chip
                                              label={`${Math.round(healthScore)}`}
                                              size="small"
                                              sx={{
                                                backgroundColor: `${color}15`,
                                                color: color,
                                                border: `1px solid ${color}40`,
                                                fontWeight: 700,
                                                fontSize: '0.7rem',
                                                height: 22,
                                                minWidth: 45,
                                              }}
                                            />
                                          );
                                        })()}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          py: 0.25,
                                          px: 2,
                                        }}
                                      >
                                        <Tooltip
                                          title="View Logs"
                                          arrow
                                          placement="top"
                                        >
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleViewLogs(
                                                run.workflow_run_id,
                                              )
                                            }
                                            sx={{
                                              color: '#5965A6',
                                              '&:hover': {
                                                backgroundColor:
                                                  'rgba(89, 101, 166, 0.08)',
                                              },
                                            }}
                                          >
                                            <Visibility sx={{ fontSize: 18 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                            </React.Fragment>
                          );
                        })}
                      </AnimatePresence>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {/* Logs Viewer Dialog */}
              <LogsViewer
                open={logsDialogOpen}
                onClose={() => setLogsDialogOpen(false)}
                workflowRunId={selectedWorkflowRunId}
              />
            </Box>
          </motion.div>
        </Box>
      </Box>

      {/* Workflow Selection Toast */}
      <Snackbar
        open={showWorkflowToast}
        autoHideDuration={3000}
        onClose={() => setShowWorkflowToast(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: 'rgb(235, 118, 23)',
            color: '#ffffff',
            fontWeight: 500,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(89, 101, 166, 0.3)',
          },
        }}
      >
        <Alert
          onClose={() => setShowWorkflowToast(false)}
          severity="info"
          sx={{
            width: '100%',
            backgroundColor: 'rgb(235, 118, 23)',
            color: '#ffffff',
            '& .MuiAlert-icon': {
              color: '#ffffff',
            },
            '& .MuiAlert-message': {
              color: '#ffffff',
            },
          }}
        >
          <strong>{selectedWorkflowName}</strong> is selected for brief details
          reports
        </Alert>
      </Snackbar>
    </motion.div>
  );
};

export default Reports;
